import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, X } from "lucide-react";
import clsx from "clsx";

/**
 * Promise-based confirm + prompt dialogs that match the app's grandMA3
 * aesthetic. Drop-in replacement for `window.confirm()` / `window.prompt()`.
 *
 * Usage:
 *   const ok = await confirm({ title: "Delete task?", body: "Cannot undo…", danger: true });
 *   if (!ok) return;
 *
 *   const value = await prompt({ title: "Until date", default: "2026-06-01", type: "date" });
 *   if (value == null) return;  // cancelled
 *
 * Mount <DialogHost /> once at the App root.
 */

// ── Singleton subscriber pattern ───────────────────────────────────────
const listeners = new Set();
let nextId = 1;

function pushDialog(spec) {
  return new Promise((resolve) => {
    const id = nextId++;
    const entry = { id, ...spec, resolve };
    listeners.forEach((cb) => cb({ type: "add", entry }));
  });
}

export function confirm({
  title = "Are you sure?",
  body = "",
  confirmLabel = "CONFIRM",
  cancelLabel = "CANCEL",
  danger = false,
} = {}) {
  return pushDialog({ kind: "confirm", title, body, confirmLabel, cancelLabel, danger });
}

export function prompt({
  title = "Enter a value",
  body = "",
  default: defaultValue = "",
  placeholder = "",
  type = "text",  // "text" | "date" | "number"
  confirmLabel = "OK",
  cancelLabel = "CANCEL",
} = {}) {
  return pushDialog({ kind: "prompt", title, body, defaultValue, placeholder, type, confirmLabel, cancelLabel });
}

/**
 * 2-3 button choice dialog. Returns the chosen option's `value` or null if
 * the user dismissed via Esc / overlay click / × close button.
 *
 *   const choice = await choose({
 *     title: "Delete parent task?",
 *     body: "This task has 3 subtasks.",
 *     danger: true,
 *     options: [
 *       { label: "DELETE SUBTASKS TOO", value: "cascade", danger: true },
 *       { label: "KEEP SUBTASKS", value: "orphan" },
 *     ],
 *   });
 */
export function choose({
  title = "Choose an option",
  body = "",
  options = [],
  cancelLabel = "CANCEL",
  danger = false,
} = {}) {
  return pushDialog({ kind: "choose", title, body, options, cancelLabel, danger });
}

// ── Host component (mount once at App root) ────────────────────────────
export function DialogHost() {
  const [stack, setStack] = useState([]);

  useEffect(() => {
    const onEvent = (e) => {
      if (e.type === "add") setStack((s) => [...s, e.entry]);
    };
    listeners.add(onEvent);
    return () => listeners.delete(onEvent);
  }, []);

  const resolve = (entry, value) => {
    entry.resolve(value);
    setStack((s) => s.filter((x) => x.id !== entry.id));
  };

  if (stack.length === 0) return null;
  return createPortal(
    <>
      {stack.map((entry) => (
        <DialogShell key={entry.id} entry={entry} onResolve={resolve} />
      ))}
    </>,
    document.body
  );
}

function DialogShell({ entry, onResolve }) {
  const [value, setValue] = useState(entry.defaultValue || "");
  const inputRef = useRef(null);
  const confirmRef = useRef(null);

  // Autofocus the most useful control. For prompt → input; for confirm/choose → primary button.
  useEffect(() => {
    const t = setTimeout(() => {
      if (entry.kind === "prompt") {
        inputRef.current?.focus();
        inputRef.current?.select?.();
      } else {
        confirmRef.current?.focus();
      }
    }, 50);
    return () => clearTimeout(t);
  }, [entry.kind]);

  // Esc → cancel; Enter → primary action (skip when focused inside a textarea).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onResolve(entry, entry.kind === "prompt" ? null : entry.kind === "choose" ? null : false);
      } else if (e.key === "Enter" && !e.shiftKey) {
        const tag = (e.target.tagName || "").toLowerCase();
        if (tag === "textarea") return;
        e.preventDefault();
        if (entry.kind === "prompt") onResolve(entry, value);
        else if (entry.kind === "choose") onResolve(entry, entry.options?.[0]?.value ?? null);
        else onResolve(entry, true);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [entry, value, onResolve]);

  const dismissValue = entry.kind === "prompt" ? null : entry.kind === "choose" ? null : false;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={() => onResolve(entry, dismissValue)}
      data-testid={`dialog-overlay-${entry.id}`}
    >
      <div
        className="bg-bg-surface border border-line-strong shadow-[0_0_64px_rgba(0,0,0,0.7)] w-[480px] max-w-[95vw]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        data-testid={`dialog-${entry.id}`}
      >
        <header className="flex items-start justify-between px-4 py-3 border-b border-line-strong bg-bg-elevated">
          <div className="flex items-start gap-2 min-w-0">
            {entry.danger && <AlertTriangle className="w-4 h-4 text-red shrink-0 mt-0.5" />}
            <div className="min-w-0">
              <div className={clsx("mono text-2xs uppercase tracking-[0.2em]", entry.danger ? "text-red" : "text-amber")}>
                {entry.danger ? "DESTRUCTIVE" : entry.kind === "prompt" ? "INPUT" : entry.kind === "choose" ? "CHOOSE" : "CONFIRM"}
              </div>
              <div className="text-sm text-ink mt-0.5">{entry.title}</div>
            </div>
          </div>
          <button
            onClick={() => onResolve(entry, dismissValue)}
            className="btn-ghost shrink-0"
            data-testid={`dialog-close-${entry.id}`}
          >
            <X className="w-3 h-3" />
          </button>
        </header>

        <div className="px-4 py-3 space-y-3">
          {entry.body && (
            <div className="text-sm text-ink-dim whitespace-pre-line leading-relaxed" data-testid={`dialog-body-${entry.id}`}>
              {entry.body}
            </div>
          )}
          {entry.kind === "prompt" && (
            <input
              ref={inputRef}
              type={entry.type || "text"}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={entry.placeholder || ""}
              className="w-full bg-bg-elevated text-sm p-2 outline-none border border-line focus:border-amber"
              data-testid={`dialog-input-${entry.id}`}
            />
          )}
        </div>

        <footer className="px-4 py-3 border-t border-line bg-bg-elevated flex items-center justify-end gap-2 flex-wrap">
          <button
            onClick={() => onResolve(entry, dismissValue)}
            className="btn-ghost"
            data-testid={`dialog-cancel-${entry.id}`}
          >
            {entry.cancelLabel}
          </button>
          {entry.kind === "choose" ? (
            entry.options.map((opt, idx) => (
              <button
                key={opt.value}
                ref={idx === 0 ? confirmRef : null}
                onClick={() => onResolve(entry, opt.value)}
                className={clsx(
                  "mono text-2xs uppercase tracking-widest px-3 py-1.5 border transition-colors flex items-center gap-1",
                  opt.danger
                    ? "border-red text-red hover:bg-red/10"
                    : opt.primary !== false
                    ? "border-amber text-amber hover:bg-amber-glow"
                    : "border-line text-ink-mute hover:text-ink hover:border-line-strong"
                )}
                data-testid={`dialog-choice-${opt.value}-${entry.id}`}
              >
                {opt.label}
              </button>
            ))
          ) : (
            <button
              ref={confirmRef}
              onClick={() => onResolve(entry, entry.kind === "prompt" ? value : true)}
              className={clsx(
                "mono text-2xs uppercase tracking-widest px-3 py-1.5 border transition-colors flex items-center gap-1",
                entry.danger
                  ? "border-red text-red hover:bg-red/10"
                  : "border-amber text-amber hover:bg-amber-glow"
              )}
              data-testid={`dialog-confirm-${entry.id}`}
            >
              {entry.confirmLabel}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}
